import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { CreateCustomFieldDTO, UpdateCustomFieldDTO } from '@infrastructure/product/dto/CustomFieldDTO';

export const useCustomFields = () => {
  const queryClient = useQueryClient();

  const { data: customFieldsResponse, isLoading } = useQuery({
    queryKey: ['custom-fields'],
    queryFn: async () => {
      const result = await container.customFieldRepository.getCustomFields();
      if (result.status === 'error') throw new Error(result.error.message);
      return result;
    },
    refetchOnWindowFocus: false,
  });

  const invalidateCustomFields = () => {
    queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
    queryClient.invalidateQueries({ queryKey: ['product'] });
  };

  const { mutateAsync: createCustomField } = useMutation({
    mutationFn: async (data: CreateCustomFieldDTO) => {
      const result = await container.customFieldRepository.createCustomField(data);
      if (result.status === 'error') throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: invalidateCustomFields,
  });

  const { mutateAsync: updateCustomField } = useMutation({
    mutationFn: async ({
      customFieldId,
      data,
    }: {
      customFieldId: string;
      data: UpdateCustomFieldDTO;
    }) => {
      const result = await container.customFieldRepository.updateCustomField(
        customFieldId,
        data
      );
      if (result.status === 'error') throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: invalidateCustomFields,
  });

  return {
    customFields: customFieldsResponse,
    isLoading,
    createCustomField,
    updateCustomField,
  };
};
